import { z } from 'zod';
import { ContentProfile, Platform } from './profile.js';
import { WeekNumber } from './requests.js';
/**
 * Structured plan shape. The Anthropic service generates the whole month in one
 * call (see `generateMonth`) and emits all four `WeekPlan`s at once. Each
 * `ContentIdea` is a brief — one platform-specific idea per slot (hook + angle),
 * not a finished caption. Finished captions are produced on demand by the draft
 * endpoint and stored as `Draft`s alongside the plan.
 */
export const PostDay = z.enum(['monday', 'wednesday', 'friday']);
/** The four Content Bucket families an idea can draw from. */
export const ContentBucket = z.enum([
    'personality',
    'practical',
    'persuasive',
    'promotional',
]);
/**
 * A single content idea (Mon/Wed/Fri): one platform, a hook, and an angle.
 * `id` is assigned server-side after generation (`${week}-${day}`) so drafts can
 * point at a specific idea; the model never produces it.
 */
export const ContentIdea = z.object({
    id: z.string().trim().min(1).optional(),
    day: PostDay,
    pillar: z.string().trim().min(1),
    bucket: ContentBucket,
    platform: Platform,
    hook: z.string().trim().min(1),
    angle: z.string().trim().min(1),
    visual: z.string().trim().min(1).optional(),
});
export const WeekPlan = z.object({
    week: WeekNumber,
    dateRange: z.string().trim().default(''),
    ideas: z.array(ContentIdea).min(1),
});
/** Response from `POST /api/generate-month` — the full month in one shot. */
export const GenerateMonthResponse = z.object({
    weeks: z.array(WeekPlan).length(4),
});
export const FullPlan = z.object({
    profile: ContentProfile,
    month: z.string().min(1).max(40),
    weeks: z.array(WeekPlan).length(4),
});
/** A finished, ready-to-publish caption generated on demand for one idea. */
export const Draft = z.object({
    ideaId: z.string().trim().min(1),
    platform: Platform,
    caption: z.string().trim().min(1),
    visual: z.string().trim().min(1).optional(),
    createdAt: z.string(), // ISO 8601
});
/** Response from `POST /api/draft`. */
export const DraftResponse = z.object({
    draft: Draft,
});
export const FinalizeRequest = z.object({
    email: z.string().email().max(254),
    name: z.string().min(1).max(120).optional(),
    plan: FullPlan,
});
export const FinalizeResponse = z.object({
    planId: z.string().uuid(),
    url: z.string().url(),
});
export const StoredPlan = z.object({
    planId: z.string().uuid(),
    createdAt: z.string(), // ISO 8601
    plan: FullPlan,
    /** Finished captions generated on demand, keyed by `ContentIdea.id`. */
    drafts: z.record(z.string(), Draft).optional(),
});
//# sourceMappingURL=plan.js.map